// QR Kiosk v2 — KV2-2 service reports LIST (read-only). v1 KioskServiceReportsList
// semantics × the KV2-1-e route-scope pattern. Open/Archive tabs are bound to the
// :filter URL (navigation, NOT local state); ONE fetch, client-side status split;
// per-row urgency pip (PG-7); count pill only when > 0.
// Kiosk review §2: rows are DISTINCT CARDS (v1 virtue restored — rounded, shadow,
// colored left accent, 10px gaps, big tap targets), not flat divided rows. Accent
// encodes urgency on Open rows (red/amber/green) and solved-green on Archive rows.
import React, { useEffect, useMemo } from "react";
import { NavigateFunction, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import { getMachine, getServiceReports } from "../../publicSlice";
import { ServiceReport, ServiceReportStatusCode } from "../../models/serviceReportsModels";
import { KPage } from "../shared/KPage";
import { Glyph, GD } from "../shared/glyphs";
import { CountPill } from "../shared/StatusPip";
import { EmptyCard } from "../shared/EmptyCard";
import { ErrorCallout } from "../shared/ErrorCallout";
import { CachedErrorState } from "../shared/CachedErrorState";
import { useSurfaceState } from "../shared/useSurfaceState";
import { useKioskRouteMachine } from "../shared/useKioskRouteMachine";
import { formatClockTime } from "../shared/clock";
import { usePressed } from "../shared/usePressed";
import { KDate, UrgencyTag } from "./ReportBits";
import { URGENCY_META } from "./reportUtils";
import { ReportsListSkeleton } from "./ReportSkeletons";

const REPORTS_QUERY = { statusCodes: ["InProgress", "Solved"] };

interface ReportRowProps {
  report: ServiceReport;
  index: number;
  archive: boolean;
  onOpen: () => void;
}

// Local component (not a .map inline) because it calls usePressed per row (KV2-2-j).
// Review §2: each row is its own card (v1 style) — border, radius, shadow, colored
// 4px left accent (urgency-tied when mapped; amber fallback on Open, green on Archive).
function ReportRow({ report, index, archive, onOpen }: ReportRowProps) {
  const { pressHandlers, pressStyle } = usePressed();
  const urgencyTone = report.urgency != null && URGENCY_META[report.urgency] ? URGENCY_META[report.urgency].tone : undefined;
  const accent = archive ? "var(--green)" : `var(${urgencyTone || "--amber"})`;
  return (
    <button
      type="button"
      {...pressHandlers}
      onClick={onOpen}
      className="kv2-enter"
      style={{
        width: "100%", textAlign: "left", fontFamily: "inherit", cursor: "pointer",
        display: "flex", alignItems: "center", gap: 12, padding: "14px 14px 14px 18px",
        background: "var(--card)", position: "relative", overflow: "hidden",
        border: "1px solid var(--border)", borderRadius: 13, boxShadow: "var(--shadow)",
        WebkitTapHighlightColor: "transparent",
        animationDelay: `${Math.min(index * 0.05, 0.4)}s`, ...pressStyle,
      }}
    >
      <span
        style={{
          position: "absolute", left: 0, top: 12, bottom: 12, width: 4,
          borderRadius: "0 4px 4px 0", background: accent,
        }}
      />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 600, color: "var(--tier-accent)" }}>
            #{report.number}
          </span>
          <UrgencyTag urgency={report.urgency} />
        </span>
        <span
          style={{
            display: "block", fontSize: 15.5, fontWeight: 700, color: "var(--ink)", marginTop: 4,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}
        >
          {report.title}
        </span>
        <span
          style={{
            display: "block", fontFamily: "var(--font-mono)", fontSize: 11.5,
            color: "var(--muted)", marginTop: 4, letterSpacing: "0.03em",
          }}
        >
          <KDate date={report.openDate} />
          {report.closeDate && (
            <>
              {" — "}
              <KDate date={report.closeDate} />
            </>
          )}
        </span>
      </span>
      <span style={{ color: "var(--muted)", display: "inline-flex", flexShrink: 0 }}>
        <Glyph d={GD.chev} size={18} />
      </span>
    </button>
  );
}

interface TabBarProps {
  id?: string;
  isOpen: boolean;
  openCount: number;
  navigate: NavigateFunction;
  tOpen: string;
  tArchive: string;
}

function TabBar({ id, isOpen, openCount, navigate, tOpen, tArchive }: TabBarProps) {
  const tabStyle = (active: boolean): React.CSSProperties => ({
    flex: 1, border: 0, fontFamily: "inherit", fontSize: 14.5, cursor: "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
    background: active ? "var(--accent-soft)" : "transparent",
    boxShadow: active ? "inset 0 -2px 0 var(--accent)" : "none",
    color: active ? "var(--ink)" : "var(--muted)", fontWeight: active ? 700 : 500,
  });
  // Tap on the already-active tab = no-op. replace: true — a view toggle, not a journey
  // step (KV2-2-a); the URL still changes so deep links are preserved.
  const goTab = (open: boolean) => {
    if (open === isOpen) return;
    navigate(`/public-v2/machines/${id}/service-reports/${open ? "open" : "archive"}`, { replace: true });
  };
  return (
    <div
      className="kv2-enter"
      style={{
        display: "flex", margin: "10px 16px 0", height: 44, borderRadius: 10,
        border: "1px solid var(--border)", background: "var(--card)", overflow: "hidden", flexShrink: 0,
      }}
    >
      <button type="button" onClick={() => goTab(true)} aria-current={isOpen ? "page" : undefined} style={tabStyle(isOpen)}>
        {tOpen}
        {openCount > 0 && <CountPill n={openCount} />}
      </button>
      <button type="button" onClick={() => goTab(false)} aria-current={!isOpen ? "page" : undefined} style={tabStyle(!isOpen)}>
        {tArchive}
      </button>
    </div>
  );
}

export function ReportsListV2() {
  const { id, filter } = useParams<{ id: string; filter: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("public");
  const isOpen = filter === "open"; // v1 verbatim — unwhitelisted (any other value => archive)
  const { serviceReports } = useAppSelector((s) => s.public);

  // Route-scoped machine (KV2-1-e) via the shared resolver — resolves + backfills the
  // guest's curated payload so the internalNumber-keyed reports fetch below actually fires.
  const m = useKioskRouteMachine(id);

  useEffect(() => {
    if (id && !m) dispatch(getMachine(id));
  }, [id, m, dispatch]);

  // ONE fetch, both statuses — split is client-side (v1 verbatim).
  const fetchReports = () => {
    if (m?.internalNumber) dispatch(getServiceReports({ machineNumber: m.internalNumber, query: REPORTS_QUERY }));
  };
  useEffect(() => {
    if (m?.internalNumber) dispatch(getServiceReports({ machineNumber: m.internalNumber, query: REPORTS_QUERY }));
  }, [m?.internalNumber, dispatch]);

  const stMachine = useSurfaceState("machine", id);
  const stReports = useSurfaceState("serviceReports", m?.internalNumber);

  // KV2-5 BUG-3: scope by the reports surface's fulfilled key (the machineNumber the
  // cache was loaded for), NOT the old some/empty heuristic — an empty result cached
  // for machine B no longer reads as "machine A has no reports".
  const scopedReports = serviceReports != null && stReports.loadedForKey ? serviceReports : undefined;

  const openRows = useMemo(
    () => (scopedReports ?? []).filter((r) => r.statusCode === ServiceReportStatusCode.inProgress),
    [scopedReports]
  );
  const solvedRows = useMemo(
    () => (scopedReports ?? []).filter((r) => r.statusCode === ServiceReportStatusCode.solved),
    [scopedReports]
  );
  const rows = isOpen ? openRows : solvedRows;

  const loadErr = (onRetry: () => void) => (
    <ErrorCallout
      title={translate("kioskV2ReportsLoadFailedTitle")}
      hint={translate("kioskV2DashboardLoadFailedHint")}
      onRetry={onRetry}
      retryLabel={translate("kioskV2Retry")}
    />
  );

  // Normal body for the active tab (rows card OR EmptyCard). Only meaningful when
  // scopedReports is present; this is what the cached-dimmed wrapper dims.
  const renderNormalBody = (): React.ReactNode => {
    if (rows.length === 0) {
      const faultAction = m?.internalNumber ? (
        <button
          type="button"
          onClick={() => navigate(`/public-v2/service-reports/create?machine=${m.internalNumber}`)}
          style={{
            marginTop: 16, height: 52, width: "100%", borderRadius: 11, fontFamily: "inherit",
            fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center",
            gap: 8, cursor: "pointer",
            border: "1px solid color-mix(in oklab, var(--red) 42%, var(--border))",
            background: "color-mix(in oklab, var(--red) 12%, var(--card))",
            color: "color-mix(in oklab, var(--red) 85%, var(--ink))",
          }}
        >
          <Glyph d={GD.warn} size={18} />
          {translate("kioskReportFault")}
        </button>
      ) : undefined;
      return isOpen ? (
        <EmptyCard glyph={GD.check} title={translate("noOpenReports")} hint={translate("kioskV2NoOpenReportsHint")} action={faultAction} />
      ) : (
        <EmptyCard glyph={GD.hist} title={translate("noSolvedReports")} hint={translate("kioskV2NoArchiveHint")} />
      );
    }
    // Review §2: plain gap column — each ReportRow is its own card (no container chrome).
    return (
      <div
        style={{
          margin: "12px 14px 0", display: "flex", flexDirection: "column", gap: 10, flexShrink: 0,
        }}
      >
        {rows.map((report, index) => (
          <ReportRow
            key={report.number}
            report={report}
            index={index}
            archive={!isOpen}
            onOpen={() => navigate(`/public-v2/service-reports/${report.number}`)}
          />
        ))}
      </div>
    );
  };

  // §5 tree over two surfaces. Machine (context) is the outer surface; the reports
  // rows-card (body) is what gets dimmed. BUG-1: skeleton covers idle AND pending.
  let body: React.ReactNode;
  if (!m) {
    body = stMachine.failed
      ? loadErr(() => { if (id) dispatch(getMachine(id)); })
      : <ReportsListSkeleton />;
  } else if (!scopedReports) {
    body = stReports.failed ? loadErr(fetchReports) : <ReportsListSkeleton />;
  } else {
    const machineFailed = stMachine.failed; // outer surface wins the callout when both failed
    if (machineFailed || stReports.failed) {
      body = (
        <CachedErrorState
          title={translate("kioskV2ReportsLoadFailedTitle")}
          hint={translate("kioskV2DashboardLoadFailedHint")}
          onRetry={machineFailed ? () => { if (id) dispatch(getMachine(id)); } : fetchReports}
          retryLabel={translate("kioskV2Retry")}
          cachedAt={stReports.loadedAt}
          cachedLabel={stReports.loadedAt != null ? translate("kioskV2ShowingCachedFrom", { time: formatClockTime(stReports.loadedAt) }) : undefined}
        >
          {renderNormalBody()}
        </CachedErrorState>
      );
    } else {
      body = renderNormalBody();
    }
  }

  return (
    <KPage
      titleKey="kioskV2ReportsTitle"
      glyph={GD.clip}
      count={isOpen && openRows.length > 0 ? openRows.length : undefined}
      machineNumber={id}
    >
      <TabBar
        id={id}
        isOpen={isOpen}
        openCount={openRows.length}
        navigate={navigate}
        tOpen={translate("kioskV2TabOpen")}
        tArchive={translate("kioskV2TabArchive")}
      />
      {body}
    </KPage>
  );
}

export default ReportsListV2;
